/**
 * Mesures du rapport d'intervention (décision 24 du 19/10/2026), gabarit
 * « froid » : pressions haute et basse, surchauffe, sous-refroidissement et
 * températures. Chaque mesure a une unité fixe et une plage plausible : le
 * serveur les impose, l'écran ne fait que s'y conformer. Une seule valeur par
 * mesure et par rapport (la saisir à nouveau remplace la précédente).
 */

export interface ReportMeasurementType {
  key: string;
  label: string;
  /** Libellé court pour une ligne d'historique. */
  short: string;
  unit: string;
  min: number;
  max: number;
}

export const REPORT_MEASUREMENT_TYPES = [
  { key: 'hp_pressure', label: 'Pression haute (HP)', short: 'HP', unit: 'bar', min: 0, max: 100 },
  { key: 'bp_pressure', label: 'Pression basse (BP)', short: 'BP', unit: 'bar', min: -1, max: 100 },
  { key: 'superheat', label: 'Surchauffe', short: 'Surchauffe', unit: 'K', min: -50, max: 150 },
  { key: 'subcooling', label: 'Sous-refroidissement', short: 'Sous-refroid.', unit: 'K', min: -50, max: 150 },
  { key: 'temp_suction', label: 'Température d’aspiration', short: 'T aspiration', unit: '°C', min: -100, max: 250 },
  { key: 'temp_discharge', label: 'Température de refoulement', short: 'T refoulement', unit: '°C', min: -100, max: 250 },
  { key: 'temp_ambient', label: 'Température ambiante', short: 'T ambiante', unit: '°C', min: -100, max: 250 },
  { key: 'temp_cabinet', label: 'Température de l’enceinte', short: 'T enceinte', unit: '°C', min: -100, max: 250 },
] as const satisfies readonly ReportMeasurementType[];

export type ReportMeasurementKey = (typeof REPORT_MEASUREMENT_TYPES)[number]['key'];

export const REPORT_MEASUREMENT_KEYS = REPORT_MEASUREMENT_TYPES.map((type) => type.key) as [
  ReportMeasurementKey, ...ReportMeasurementKey[],
];

export function reportMeasurementType(key: string): ReportMeasurementType | undefined {
  return REPORT_MEASUREMENT_TYPES.find((type) => type.key === key);
}

/** Libellé d'une mesure ; une clé d'avant le gabarit garde son texte. */
export function reportMeasurementLabel(key: string, short = false): string {
  const type = reportMeasurementType(key);
  return type ? (short ? type.short : type.label) : key;
}

/** Valeur lisible, virgule décimale et unité : « 18,5 bar ». */
export function formatReportMeasurement(value: number | string, unit: string): string {
  const number = Number(value);
  const shown = Number.isFinite(number) ? String(Math.round(number * 100) / 100).replace('.', ',') : String(value);
  return `${shown} ${unit}`.trim();
}

/** La valeur tombe-t-elle dans la plage plausible de cette mesure ? */
export function reportMeasurementInRange(key: string, value: number): boolean {
  const type = reportMeasurementType(key);
  return Boolean(type) && Number.isFinite(value) && value >= type!.min && value <= type!.max;
}

/** Mesures dans l'ordre du gabarit (HP, BP, surchauffe…) ; une clé d'avant le gabarit passe après. */
export function sortReportMeasurements<T extends { key: string }>(list: readonly T[]): T[] {
  const rank = (key: string): number => {
    const index = REPORT_MEASUREMENT_TYPES.findIndex((type) => type.key === key);
    return index < 0 ? REPORT_MEASUREMENT_TYPES.length : index;
  };
  return [...list].sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key));
}
